/**
 * particleField — geometry for radial particle bursts.
 *
 * Split out from the component so the maths is unit-testable without rendering,
 * and so every celebration draws from the same distribution instead of each one
 * inventing its own scatter.
 *
 * Positions are DETERMINISTIC for a given (seed, count, variant). Two things
 * depend on that: re-renders must not make particles jump to new angles, and
 * tests need to assert real numbers rather than "something random happened".
 */

/** Mulberry32 — tiny seeded PRNG. Same seed in, same sequence out. */
const makeRng = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export const PARTICLE_VARIANTS = {
  /** Small four-point stars — the default celebration confetti. */
  sparkle: {
    minDistance: 60,
    maxDistance: 190,
    minSize: 4,
    maxSize: 11,
    spinDeg: 220,
    colors: ["#c4b5fd", "#a78bfa", "#f0abfc", "#fde68a", "#ffffff"],
  },
  /** Elongated light streaks that shoot further and fade fast. */
  streak: {
    minDistance: 110,
    maxDistance: 300,
    minSize: 2,
    maxSize: 4,
    spinDeg: 0,
    colors: ["#e9d5ff", "#c4b5fd", "#ffffff"],
  },
  /** Rainbow foil chips — for holographic/pack moments. */
  foil: {
    minDistance: 50,
    maxDistance: 165,
    minSize: 5,
    maxSize: 13,
    spinDeg: 320,
    colors: ["#f472b6", "#7dd3fc", "#fde047", "#a78bfa", "#6ee7b7"],
  },
};

export const DEFAULT_VARIANT = "sparkle";

const lerp = (min, max, t) => min + (max - min) * t;

/**
 * Build the particle list for one burst.
 *
 * Angles are jittered around an even spread rather than fully random — pure
 * randomness clumps, which reads as a bug ("why is one side empty?").
 *
 * @returns {Array<{id,angle,distance,size,color,delayMs,spin}>}
 */
export const buildParticles = ({
  count = 24,
  variant = DEFAULT_VARIANT,
  seed = 1,
  spreadDeg = 360,
  startAngleDeg = 0,
} = {}) => {
  const cfg = PARTICLE_VARIANTS[variant] || PARTICLE_VARIANTS[DEFAULT_VARIANT];
  const safeCount = Math.max(0, Math.floor(count));
  const rng = makeRng(seed);
  const step = safeCount > 0 ? spreadDeg / safeCount : 0;

  const particles = [];
  for (let i = 0; i < safeCount; i++) {
    // Even spread + up to half a step of jitter, so it scatters without clumping.
    const jitter = (rng() - 0.5) * step;
    const angle = startAngleDeg + i * step + jitter;

    particles.push({
      id: i,
      angle,
      distance: Math.round(lerp(cfg.minDistance, cfg.maxDistance, rng())),
      size: Math.round(lerp(cfg.minSize, cfg.maxSize, rng())),
      color: cfg.colors[Math.floor(rng() * cfg.colors.length)],
      // Staggered launch reads as an explosion rather than a ring expanding.
      delayMs: Math.round(rng() * 220),
      spin: Math.round((rng() - 0.5) * 2 * cfg.spinDeg),
    });
  }
  return particles;
};

/**
 * True when the user asked the OS to keep motion down. Celebrations must degrade
 * to a static state rather than being merely shorter.
 */
export const prefersReducedMotion = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};
